import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';
import { addYears } from 'date-fns';

import Field from 'core/components/form/form.v2';
import { ActionButton } from 'core/components/action-button';
import useSession from 'core/hooks/use-session';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import { getUnderConstructionTheftDateBounds } from './dwelling-under-construction.utils';
import useStyles from './sub-risk.styles';

const DwellingUnderConstructionForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const isUW = Boolean(useSession().canApproveUWReview);

  const isApplied =
    !!getIn(values, `${fieldName}.resUnderConstructionLimit`) ||
    !!getIn(values, `${fieldName}.resUnderConstructionEffectiveDate`);

  // "Remove" an always-rendered coverage section = clear its fields back to the un-applied state (#32149).
  const handleRemove = () => {
    setFieldValue(`${fieldName}.resUnderConstructionLimit`, null);
    setFieldValue(`${fieldName}.resUnderConstructionEffectiveDate`, null);
  };

  // Policy term for the effective-date bounds. Prefer the offer/policy effective + expiration;
  // fall back to farmEffectiveDate (+1yr) the way the scheduled-coverage forms do (#32149).
  const farmEffectiveDate = values.global?.farmEffectiveDate;
  const policyEffectiveDate = values.effectiveDate || farmEffectiveDate;
  const policyExpirationDate =
    values.expirationDate ||
    (farmEffectiveDate ? addYears(new Date(farmEffectiveDate), 1).toISOString().split('T')[0] : undefined);

  // Agents: today-5 .. today+60, clamped to the policy term. Underwriters: the full policy term.
  const { minDate, maxDate } = getUnderConstructionTheftDateBounds({
    isUnderwriter: isUW,
    policyEffectiveDate,
    policyExpirationDate
  });

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Dwelling under construction (theft)</div>}
      contentRight={
        isApplied ? (
          <div className={classes.appliedActions}>
            <AppliedTag />
            {!disabled && (
              <ActionButton
                type="delete"
                size="small"
                dataTestId="dwelling-under-construction-remove"
                alt="Remove dwelling under construction (theft) coverage"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove();
                }}
              />
            )}
          </div>
        ) : null
      }
      initialOpen={false}
    >
      <Grid container spacing={2}>
        <Field
          xs={6}
          type="numeric"
          id={`${fieldName}.resUnderConstructionLimit`}
          name={`${fieldName}.resUnderConstructionLimit`}
          mode="dark"
          label="Coverage limit"
          format={{ thousandSeparator: true, prefix: '$' }}
          disabled={disabled}
        />
        <Field
          xs={6}
          type="date"
          id={`${fieldName}.resUnderConstructionEffectiveDate`}
          name={`${fieldName}.resUnderConstructionEffectiveDate`}
          mode="dark"
          label="Effective date"
          disabled={disabled}
          defaultToEmpty
          // DatePicker defaults disableFuture=true, which would cap the calendar at today and make
          // the agent (today+60) / UW (policy expiration) upper bound unreachable — opt out (#32149).
          disableFuture={false}
          minDate={minDate}
          maxDate={maxDate}
        />
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

DwellingUnderConstructionForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

DwellingUnderConstructionForm.defaultProps = {
  disabled: false
};

export default observer(DwellingUnderConstructionForm);
